import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Compass, Ruler, Users, Sparkles, ArrowRight, Briefcase } from "lucide-react";
import SEO from "@/components/SEO";
import GeoDefinition, { buildDefinedTerm, buildFaqJsonLd } from "@/components/GeoDefinition";
import SectionHeading from "@/components/SectionHeading";
import LeadForm from "@/components/LeadForm";

const FOUNDER_DEFINITION =
  "Der Gründerpfad von PINE Systems unterstützt Interessenten ohne konkrete Fläche dabei, aus einer Idee ein prüfbares Standortvorhaben zu machen. Region, Zielgruppe, Betreiberrolle und mögliches Systemprofil (PVRA, AR_ENA, PINE VR, Hotel VR, PINE Places) werden strukturiert eingeordnet, bevor eine Fläche gesucht wird.";

const FOUNDER_QA = [
  {
    q: "Kann ich mich melden, wenn ich noch keine Fläche habe?",
    a: "Ja. Wenn Sie mit PINE Systems gründen möchten, aber noch keine passende Fläche haben, ist ein Gründer-Erstgespräch der sinnvolle erste Schritt. Wir klären gemeinsam, welches Systemprofil grundsätzlich zu Ihrer Idee, Region, Zielgruppe und Betreiberrolle passen kann.",
  },
  {
    q: "Unterstützt PINE Systems bei der Standortsuche?",
    a: "PINE Systems kann Anforderungen an geeignete Flächen, Standortlogik und Produktfit einordnen. Die konkrete Suche, Anmietung, rechtliche Prüfung und wirtschaftliche Entscheidung bleiben beim Gründer beziehungsweise Betreiber.",
  },
  {
    q: "Welches System eignet sich für Gründer?",
    a: "Das hängt von Region, Zielgruppe, Fläche, Betreiberstruktur und Investitionslogik ab. PVRA, AR_ENA und PINE VR können je nach Ausgangslage relevant sein. Hotel VR richtet sich stärker an bestehende Hospitality-Betriebe. PINE Places kann für digitale Standort- und Markenräume relevant sein.",
  },
];

const PROBLEM_CARDS = [
  {
    icon: Compass,
    title: "Systemprofil",
    text: "Welches PINE Systems Profil passt grundsätzlich zu Ihrer Idee?",
  },
  {
    icon: MapPin,
    title: "Region",
    text: "Welche Zielgruppen, Einzugsgebiete und Nutzungsideen sind für Ihren Markt relevant?",
  },
  {
    icon: Ruler,
    title: "Standortanforderung",
    text: "Welche Fläche sollten Sie später suchen – und welche Parameter sind kritisch?",
  },
  {
    icon: Briefcase,
    title: "Betreiberrolle",
    text: "Möchten Sie selbst betreiben, investieren, entwickeln oder mit Partnern arbeiten?",
  },
];

const NEEDS = [
  {
    title: "Systemverständnis",
    text: "PVRA, AR_ENA, PINE VR, Hotel VR und PINE Places folgen unterschiedlichen Standortlogiken. Im Gründerpfad klären wir, welches Profil grundsätzlich zu Ihrer Idee passt.",
  },
  {
    title: "Standortprofil",
    text: "Aus dem Systemprofil entsteht ein Suchprofil: ungefähre Fläche, Raumhöhe, Grundriss, Lage, Gästewege, Umfeld, Betreiberflächen und Zielgruppenlogik.",
  },
  {
    title: "Gründerrolle",
    text: "Ein Standort funktioniert anders, je nachdem ob Sie selbst operativ betreiben, investieren, entwickeln oder mit einem lokalen Partner arbeiten möchten.",
  },
  {
    title: "Region und Markt",
    text: "Wir betrachten, ob Ihre Wunschregion eher zu Familien, Gruppen, Firmen, Events, Tourismus oder Competition passt.",
  },
  {
    title: "Nächster Schritt",
    text: "Aus Interesse wird noch keine Kaufentscheidung. Der nächste Schritt ist ein Gründer-Erstgespräch.",
  },
];

const PROFILES = [
  {
    title: "Gründer ohne Fläche",
    text: "Sie möchten eine eigene Erlebnisarena aufbauen, wissen aber noch nicht, welche Fläche Sie suchen sollten.",
  },
  {
    title: "Investoren",
    text: "Sie interessieren sich für moderne Erlebnisstandorte und möchten verstehen, welches Systemprofil grundsätzlich zu Ihrer Rolle passt.",
  },
  {
    title: "Unternehmer",
    text: "Sie haben Erfahrung mit Betrieb, Personal oder Standorten und suchen ein neues Angebotssystem.",
  },
  {
    title: "Standortentwickler",
    text: "Sie denken in Flächen, Regionen oder Immobilien und möchten prüfen, welche Erlebnislogik dazu passen könnte.",
  },
  {
    title: "Quereinsteiger",
    text: "Sie kommen nicht aus der Freizeitwirtschaft, möchten aber professionell und strukturiert starten.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Gründungsidee einordnen",
    text: "Sie beschreiben Region, Motivation, Zielgruppe, Betreiberrolle und Systeminteresse.",
  },
  {
    n: "02",
    title: "Gründer-Erstgespräch führen",
    text: "Wir klären Ausgangslage, Erwartungen, mögliche Systemprofile und offene Fragen.",
  },
  {
    n: "03",
    title: "Systemprofil eingrenzen",
    text: "PVRA, AR_ENA, PINE VR, Hotel VR oder PINE Places werden grundsätzlich eingeordnet.",
  },
  {
    n: "04",
    title: "Standortanforderungen ableiten",
    text: "Aus dem Systemprofil entsteht eine Orientierung, welche Fläche später gesucht oder geprüft werden sollte.",
  },
  {
    n: "05",
    title: "Fläche suchen oder Optionen prüfen",
    text: "Mit klareren Anforderungen kann die Standortsuche gezielter stattfinden.",
  },
  {
    n: "06",
    title: "In Standortprüfung wechseln",
    text: "Sobald eine konkrete Fläche vorhanden ist, geht der Prozess in die reguläre Standortprüfung über.",
  },
];

const GRUENDEN_JSONLD = [
  buildDefinedTerm({
    name: "PINE Systems Gründerpfad",
    alternateName: "Gründerpfad",
    description: FOUNDER_DEFINITION,
    url: "https://www.pinexs.de/gruenden",
  }),
  buildFaqJsonLd(FOUNDER_QA),
];

export default function Gruenden() {
  return (
    <>
      <SEO
        title="Mit PINE Systems gründen – Erlebnisarena und XR-Standort aufbauen"
        description="Sie möchten mit PINE Systems einen Erlebnisstandort gründen, haben aber noch keine Fläche? Der Gründerpfad hilft, Systemprofil, Region, Zielgruppe und Standortanforderungen einzuordnen."
        jsonLd={GRUENDEN_JSONLD}
      />

      {/* HERO */}
      <section className="relative border-b border-[rgba(255,255,255,0.08)] overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="overline" data-testid="gruenden-eyebrow">
            Gründerpfad · PINE Systems
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl md:text-6xl uppercase mt-4 max-w-5xl leading-[1.04]"
            data-testid="gruenden-headline"
          >
            Mit PINE Systems einen <br className="hidden md:block" />
            <span className="text-[#18E096]">Erlebnisstandort</span> aufbauen.
          </h1>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            Sie interessieren sich für eine eigene Arena, haben aber noch keine passende
            Fläche? Dann beginnt der Weg nicht mit dem Mietvertrag, sondern mit dem
            richtigen Standortprofil.
          </p>
          <p className="text-[#AEB7B7] mt-4 max-w-3xl text-sm md:text-base leading-relaxed">
            PINE Systems unterstützt Gründer dabei, aus einer Idee ein prüfbares
            Standortvorhaben zu machen. Gemeinsam klären wir, welches Systemprofil
            grundsätzlich zu Ihrer Region, Zielgruppe, Betreiberrolle und späteren Fläche
            passen kann.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#gruender-form" className="btn-primary" data-testid="gruenden-cta-primary">
              Gründer-Erstgespräch anfragen <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
            <Link to="/systeme" className="btn-secondary" data-testid="gruenden-cta-secondary">
              Systeme ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="01 · Ausgangslage"
            title={
              <>
                Viele Gründer suchen zuerst eine Fläche. <br className="hidden md:block" />
                Wir starten <span className="text-[#18E096]">einen Schritt früher.</span>
              </>
            }
            subtitle="Eine Fläche entscheidet nicht allein über Eignung oder späteren Betrieb. Entscheidend ist, welches Erlebnisangebot in welcher Region, für welche Zielgruppe und mit welcher Betreiberstruktur funktionieren kann."
          />
          <p className="text-[#AEB7B7] mt-8 max-w-3xl text-base leading-relaxed">
            Wer zu früh irgendeinen Raum mietet, riskiert, später an Grundriss, Raumhöhe,
            Infrastruktur, Gästewegen oder Produktfit zu scheitern. Deshalb beginnt der
            Gründerpfad bei PINE Systems mit einer strukturierten Einordnung.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-14 border-l border-t border-[rgba(255,255,255,0.08)]">
            {PROBLEM_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 hover:bg-[#0B0F10] transition-colors group min-w-0"
                  data-testid={`gruenden-problem-${i}`}
                >
                  <Icon
                    size={28}
                    strokeWidth={1.25}
                    className="text-[#18E096] group-hover:scale-110 transition-transform"
                  />
                  <div className="overline mt-6 text-[#AEB7B7]">0{i + 1}</div>
                  <h3 className="font-display text-base md:text-lg lg:text-xl uppercase mt-2 text-white leading-tight break-words [overflow-wrap:anywhere] hyphens-auto">
                    {c.title}
                  </h3>
                  <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT YOU NEED BEFORE LOCATION */}
      <section className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="02 · Vor der Flächensuche"
            title="Was Sie vor der Flächensuche klären sollten."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {NEEDS.map((n, i) => (
              <div
                key={i}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 bg-[#0B0F10] hover:bg-[#141819] transition-colors min-w-0"
                data-testid={`gruenden-need-${i}`}
              >
                <div className="font-display text-3xl text-[#18E096]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-lg md:text-xl uppercase mt-4 text-white leading-tight break-words [overflow-wrap:anywhere] hyphens-auto">
                  {n.title}
                </h3>
                <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{n.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="03 · Zielgruppe"
            title="Für wen der Gründerpfad gedacht ist."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {PROFILES.map((p, i) => (
              <div
                key={i}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 hover:bg-[#0B0F10] transition-colors group"
                data-testid={`gruenden-profile-${i}`}
              >
                <Users
                  size={22}
                  strokeWidth={1.25}
                  className="text-[#18E096] group-hover:scale-110 transition-transform"
                />
                <h3 className="font-display text-lg md:text-xl uppercase mt-5 text-white">
                  {p.title}
                </h3>
                <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="04 · Ablauf Gründerpfad"
            title="Vom Interesse zum Standortprofil."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 bg-[#0B0F10]"
                data-testid={`gruenden-step-${s.n}`}
              >
                <div className="font-display text-4xl text-[#18E096]">{s.n}</div>
                <h3 className="font-display text-lg md:text-xl uppercase mt-4 text-white leading-tight">
                  {s.title}
                </h3>
                <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <a href="#gruender-form" className="btn-primary" data-testid="gruenden-cta-process">
              Gründer-Erstgespräch anfragen <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="gruender-form" className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="overline mb-4">Erstgespräch anfragen</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] max-w-3xl">
            Ihre <span className="text-[#18E096]">Gründungsidee</span> einordnen.
          </h2>
          <p className="text-[#AEB7B7] mt-6 mb-8 max-w-3xl text-base leading-relaxed">
            Je strukturierter Sie Region, Rolle und Systeminteresse beschreiben, desto
            besser können wir das passende Gespräch vorbereiten. Wenn Sie noch ganz am
            Anfang stehen, ist auch das in Ordnung – Sie können jedes Feld freilassen.
          </p>
          <LeadForm sourcePage="gruenden" variant="founder" />
        </div>
      </section>

      {/* CTA SWITCH BACK */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-3">Hinweis</div>
            <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight">
              Sie haben doch schon eine Fläche?
            </h3>
            <p className="text-[#AEB7B7] mt-4 text-base leading-relaxed max-w-2xl">
              Wenn ein konkreter Standort vorhanden ist, geht der Prozess direkt in die
              reguläre Standortprüfung über.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/standort-pruefen"
              className="btn-secondary"
              data-testid="gruenden-switch-standort"
            >
              Zur Standortprüfung <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* GEO Definition */}
      <GeoDefinition
        term="Gründerpfad"
        shortTitle="Was ist der PINE Systems Gründerpfad?"
        overline="GEO · Definition"
        definition={FOUNDER_DEFINITION}
        qa={FOUNDER_QA}
        variant="muted"
      />

      {/* Schlussakzent */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 text-center">
          <Sparkles size={28} strokeWidth={1.25} className="text-[#18E096] mx-auto" />
          <h2 className="font-display text-2xl md:text-4xl uppercase mt-6 leading-tight max-w-3xl mx-auto">
            Aus Idee wird <span className="text-[#18E096]">Standortprofil.</span>
          </h2>
          <p className="text-[#AEB7B7] mt-5 max-w-2xl mx-auto">
            Wir beginnen mit der Frage nach dem richtigen System – nicht mit dem nächsten
            Mietvertrag.
          </p>
        </div>
      </section>
    </>
  );
}
